import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { RichTextDisplay } from "@/components/ui/rich-text-display";
import { HeroSelector } from "@/components/hero/index";
import DailyFactCard from "@/components/DailyFact";
import { useLayout } from "@/lib/layout-provider";
import { newsletterService } from "@/services/newsletterService";
import { toast } from "sonner";
import { 
  Heart, 
  Users, 
  Calendar, 
  BookOpen, 
  Music, 
  Award, 
  ArrowRight, 
  Star,
  Quote,
  Target,
  Globe,
  Sparkles,
  Clock,
  Building2,
  Handshake,
  GraduationCap,
  Camera,
  Megaphone,
  Gift,
  FileText,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { fetchFeaturedEvents, fetchTestimonials, type EventItem, type Testimonial } from "@/services/content";
import { fetchPublishedTiles, type HomepageTile } from "@/services/homepageTilesService";
import { fetchPublishedStats, type ImpactStat } from "@/services/impactStatsService";
import { useSiteConfig } from "@/lib/useSiteConfig";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { SEO } from "@/components/SEO";
import { STALE_TIMES, queryKeys } from "@/lib/query-config";
import { usePageContent } from "@/hooks/usePageContent";
import { RenderContent } from "@/components/pages/RenderContent";

// Icon map for dynamic icon rendering
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Globe,
  BookOpen,
  Users,
  Calendar,
  Heart,
  Music,
  Award,
  Star,
  Target,
  Sparkles,
  Building2,
  Handshake,
  GraduationCap,
  Camera,
  Megaphone,
  Gift,
};

const Index = () => {
  const { features, isLoading: settingsLoading } = useSiteSettings();
  const config = useSiteConfig();
  const { isLoading: layoutLoading } = useLayout();
  const { page: aboutPage } = usePageContent({ customSlug: 'about-us' });
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubmittingNewsletter, setIsSubmittingNewsletter] = useState(false);

  const staticFeaturedEvents: EventItem[] = [
    {
      id: 1001,
      title: "Community Celebration 2025",
      date: "January 20, 2025",
      time: "2:00 PM - 6:00 PM",
      description: "Annual celebration honoring our community's legacy through performances and dialogue.",
      category: "Community",
      featured: true,
      location: "",
      capacity: 0,
      registered: 0
    },
    {
      id: 1002,
      title: "Concert Series",
      date: "February 15, 2025", 
      time: "7:00 PM - 9:00 PM",
      description: "Powerful performances celebrating our heritage with local and touring artists.",
      category: "Performing Arts",
      featured: true,
      location: "",
      capacity: 0,
      registered: 0
    },
    {
      id: 1003,
      title: "Storytelling Evening",
      date: "March 15, 2025",
      time: "6:30 PM - 8:30 PM", 
      description: "Community elders share stories of our shared heritage and traditions.",
      category: "Community",
      featured: false,
      location: "",
      capacity: 0,
      registered: 0
    }
  ];

  const { data: featuredEventsData } = useQuery({
    queryKey: queryKeys.events.featured(),
    queryFn: async () => {
      const result = await fetchFeaturedEvents(3);
      return result.success ? result.data : [];
    },
    enabled: features.events.enabled,
    staleTime: STALE_TIMES.events,
  });

  const featuredEvents = (featuredEventsData && featuredEventsData.length > 0)
    ? featuredEventsData
    : staticFeaturedEvents;

  // Fetch homepage tiles from database
  const { data: tilesData, isLoading: tilesLoading, isFetching: tilesFetching } = useQuery({
    queryKey: queryKeys.homepage.tiles,
    queryFn: async () => {
      const result = await fetchPublishedTiles();
      return result.success ? result.data : [];
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  // Fetch impact stats from database
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: queryKeys.homepage.stats,
    queryFn: async () => {
      const result = await fetchPublishedStats();
      return result.success ? result.data : [];
    },
    staleTime: STALE_TIMES.impactStats,
  });

  // Static fallback for impact stats
  const staticImpactStats = [
    {
      id: 1,
      number: "100+",
      label: "Members",
      description: "Active participants in our community",
      order_index: 0,
      published: true,
      created_at: "",
      updated_at: "",
    },
    {
      id: 2,
      number: "25+",
      label: "Events",
      description: "Annual programs and celebrations",
      order_index: 1,
      published: true,
      created_at: "",
      updated_at: "",
    },
    {
      id: 3,
      number: "5+",
      label: "Years",
      description: `Serving since ${config.foundedYear}`,
      order_index: 2,
      published: true,
      created_at: "",
      updated_at: "",
    },
    {
      id: 4,
      number: "1,000+",
      label: "Lives Touched",
      description: "Community members impacted",
      order_index: 3,
      published: true,
      created_at: "",
      updated_at: "",
    }
  ];

  // Process impact stats: deduplicate, sort, and limit
  const processImpactStats = (stats: ImpactStat[]): ImpactStat[] => {
    if (!stats || stats.length === 0) return staticImpactStats;
    
    // Deduplicate by id, then by number+label combination
    const seenIds = new Set<number>();
    const seenCombos = new Set<string>();
    const unique: ImpactStat[] = [];
    
    for (const stat of stats) {
      // Skip if we've seen this ID
      if (stat.id && seenIds.has(stat.id)) continue;
      
      // Skip if we've seen this number+label combination
      const combo = `${stat.number}|${stat.label}`;
      if (seenCombos.has(combo)) continue;
      
      // Add to seen sets and unique array
      if (stat.id) seenIds.add(stat.id);
      seenCombos.add(combo);
      unique.push(stat);
    }
    
    // Sort by order_index, then by id
    unique.sort((a, b) => {
      if (a.order_index !== b.order_index) {
        return (a.order_index || 0) - (b.order_index || 0);
      }
      return (a.id || 0) - (b.id || 0);
    });
    
    // Limit to 4 stats for balanced display
    return unique.slice(0, 4);
  };

  // Only process stats if data has loaded OR if we're not loading
  // This prevents showing fallback static stats briefly before database stats load
  const impactStats: ImpactStat[] = statsLoading && !statsData ? [] : processImpactStats(statsData || []);

  // Fetch testimonials from database - content-managed only, no static fallbacks
  const { data: testimonialsData } = useQuery({
    queryKey: queryKeys.testimonials.list(),
    queryFn: async () => {
      const result = await fetchTestimonials();
      if (!result.success) {
        console.warn('[Index] Failed to fetch testimonials:', result.error?.getUserMessage());
        return [];
      }
      const data = result.data || [];
      console.log('[Index] Fetched testimonials from database:', data.length);
      return data;
    },
    enabled: features.testimonials.enabled,
    staleTime: STALE_TIMES.facts,
  });

  // Only use database testimonials - fully content-managed, no static fallbacks
  // If database is empty, the section will be hidden (see conditional rendering below)
  const testimonials: Testimonial[] = testimonialsData || [];

  // Process tiles: filter by feature flags, deduplicate, sort, and optionally add events tile
  const processTiles = (tiles: HomepageTile[]): HomepageTile[] => {
    if (!tiles || tiles.length === 0) return [];

    // Only allow published tiles to display
    const publishedTiles = tiles.filter(tile => Boolean(tile.published) === true);
    if (publishedTiles.length === 0) return [];
 
    // Filter tiles based on feature flags
    const filtered = publishedTiles.filter(tile => {
      const link = tile.link?.toLowerCase() || '';
      const title = tile.title?.toLowerCase() || '';
      
      // Filter out donation/support tiles if donation is disabled
      if ((link.includes('/donate') || link.includes('donate') || 
           title.includes('donate') || title.includes('support')) && 
          !features.donation.enabled) {
        return false;
      }
      
      // Filter out membership/join/get involved tiles if membership is disabled
      if ((link.includes('/membership') || link.includes('membership') || 
           link.includes('/join') || link.includes('join') ||
           title.includes('membership') || title.includes('join') ||
           title.includes('get involved') || title.includes('get-involved')) && 
          !features.membership.enabled) {
        return false;
      }
      
      // Filter out events tiles if events is disabled
      if ((link.includes('/events') || link.includes('events') || 
           title.includes('event')) && 
          !features.events.enabled) {
        return false;
      }
      
      // Filter out gallery tiles if gallery is disabled
      if ((link.includes('/gallery') || link.includes('gallery') || 
           title.includes('gallery')) && 
          !features.gallery.enabled) {
        return false;
      }
      
      // Filter out news tiles if news is disabled
      if ((link.includes('/news') || link.includes('news') || 
           title.includes('news')) && 
          !features.news.enabled) {
        return false;
      }
      
      // Filter out programs tiles if programs is disabled (case-insensitive)
      const lowerLink = link.toLowerCase();
      const lowerTitle = title.toLowerCase();
      if ((lowerLink.includes('/programs') || lowerLink.includes('programs') || 
           lowerTitle.includes('program')) && 
          !features.programs.enabled) {
        return false;
      }
      
      return true;
    });
    
    // Deduplicate by ID first, then by title+link combination
    const seenIds = new Set<number>();
    const seenCombos = new Set<string>();
    const unique: HomepageTile[] = [];
    
    for (const tile of filtered) {
      // Skip if we've seen this ID
      if (tile.id && seenIds.has(tile.id)) {
        if (import.meta.env.DEV) {
          console.warn('[Index] Duplicate tile ID found:', tile.id, tile.title);
        }
        continue;
      }
      
      // Skip if we've seen this title+link combination
      const combo = `${tile.title}|${tile.link}`;
      if (seenCombos.has(combo)) {
        if (import.meta.env.DEV) {
          console.warn('[Index] Duplicate tile found:', tile.title, tile.link);
        }
        continue;
      }
      
      // Add to seen sets and unique array
      if (tile.id) seenIds.add(tile.id);
      seenCombos.add(combo);
      unique.push(tile);
    }
    
    // Sort by order_index, then by id
    unique.sort((a, b) => {
      if (a.order_index !== b.order_index) {
        return (a.order_index || 0) - (b.order_index || 0);
      }
      return (a.id || 0) - (b.id || 0);
    });
    
    // Check if events tile already exists
    const hasEventsTile = unique.some(tile => 
      tile.link === '/events' || 
      tile.title.toLowerCase().includes('event')
    );
    
    // Add events tile if events are enabled and it doesn't already exist
    if (features.events.enabled && !hasEventsTile) {
      unique.push({
        id: 999,
        title: "Events",
        description: "Check out our upcoming events",
        icon: "Calendar",
        link: "/events",
        cta_text: "View Events",
        color_class: "bg-primary/10 hover:bg-primary/20 border-primary/20",
        order_index: 99,
        published: true,
        created_at: "",
        updated_at: "",
      });
    }
    
    return unique;
  };

  // Only process tiles if data has loaded OR if we're not loading
  // This prevents showing empty state briefly before data loads
  const quickAccessTiles = (tilesLoading || tilesFetching) ? [] : processTiles(tilesData || []);
  const showQuickAccessTiles = !tilesLoading && !tilesFetching && tilesData && tilesData.length > 0;
  
  // Find CTA tile for the final CTA section (look for tile with link "#cta" or "#homepage-cta")
  const ctaTile = tilesData?.find(tile => 
    tile.published && 
    (tile.link === '#cta' || 
     tile.link === '#homepage-cta' || 
     tile.link === '/#cta' ||
     tile.title.toLowerCase().includes('legacy') ||
     tile.title.toLowerCase().includes('be part'))
  );
  
  // Combine all loading states
  const isInitialLoading = settingsLoading || layoutLoading || (tilesLoading && !tilesData) || (statsLoading && !statsData);

  return (
    <div className="min-h-screen">
      <SEO
        title=""
        description={config.description}
        keywords={['community', 'events', 'programs']}
        type="website"
      />
      {/* Hero Section */}
      <HeroSelector />

      {/* Welcome Section */}
      {config.welcomeSection.enabled && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-warm">
          <div className="max-w-7xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              {config.welcomeSection.title} <span className="text-heritage">{config.welcomeSection.highlightedText}</span>
            </h2>
            <div className="text-xl text-muted-foreground max-w-4xl mx-auto mb-8">
              <RichTextDisplay content={config.welcomeSection.description || ''} />
            </div>
            {config.welcomeSection.buttonText && config.welcomeSection.buttonLink && (
              <Button variant="heritage" size="lg" asChild>
                <Link to={config.welcomeSection.buttonLink}>
                  {config.welcomeSection.buttonText}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            )}
          </div>
        </section>
      )}

      {/* Daily Fact Section */}
      {features.dailyFacts.enabled && (
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <DailyFactCard className="mx-auto" />
          </div>
        </section>
      )}

      {/* Quick Access Navigation - Content-Managed Only */}
      {showQuickAccessTiles && quickAccessTiles.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Discover What We Offer
              </h2>
              <p className="text-lg text-muted-foreground">
                Explore the many ways to get involved with our community
              </p>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6">
              {quickAccessTiles.map((tile) => {
              const IconComponent = iconMap[tile.icon] || Globe;
              return (
                <Card 
                  key={tile.id || `tile-${tile.title}-${tile.link}`} 
                  className={`${tile.color_class || 'bg-primary/10 hover:bg-primary/20 border-primary/20'} shadow-warm hover:shadow-heritage transition-all duration-300 cursor-pointer group w-full sm:w-[calc(50%-12px)] lg:w-[calc(25%-18px)] max-w-sm`}
                >
                  <CardContent className="p-8 text-center">
                    <IconComponent className="w-12 h-12 text-heritage mx-auto mb-4 group-hover:scale-110 transition-transform" />
                    <h3 className="text-xl font-bold text-foreground mb-3">{tile.title}</h3>
                    <div className="text-muted-foreground mb-6">
                      <RichTextDisplay content={tile.description || ''} />
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <Link to={tile.link}>
                        {tile.cta_text || 'Learn More'}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Featured Events */}
      {features.events.enabled && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-secondary/30">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-12">
              <div>
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Our Events
                </h2>
                <p className="text-lg text-muted-foreground">
                  Join us for upcoming events and programs
                </p>
              </div>
              <Button variant="culture" asChild>
                <Link to="/events">
                  View All Events
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredEvents.map((event, index) => (
                <Card 
                  key={index} 
                  className={`shadow-warm hover:shadow-heritage transition-shadow ${
                    event.featured ? 'ring-2 ring-heritage/20' : ''
                  }`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between mb-2">
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-heritage/10 text-heritage">
                        {event.category}
                      </span>
                      {event.featured && (
                        <div className="bg-heritage text-heritage-foreground px-2 py-1 rounded-full text-xs font-medium">
                          Featured
                        </div>
                      )}
                    </div>
                    <CardTitle className="text-xl mb-2">{event.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4 mr-2 text-heritage" />
                        {event.date}
                      </div>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Clock className="w-4 h-4 mr-2 text-heritage" />
                        {event.time}
                      </div>
                    </div>
                    <p className="text-muted-foreground mb-6">{event.description}</p>
                    <div className="flex gap-3">
                      <Button variant="heritage" size="sm" className="flex-1">
                        Register Now
                      </Button>
                      <Button variant="outline" size="sm">
                        Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Impact Statistics */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Our Impact
            </h2>
            <p className="text-lg text-muted-foreground">
              See the difference we're making in our community
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-8">
            {impactStats.map((stat, index) => (
              <Card 
                key={stat.id || `stat-${index}`} 
                className="text-center shadow-warm hover:shadow-heritage transition-shadow w-full md:w-[calc(50%-1rem)] lg:w-[calc(25%-1.5rem)]"
              >
                <CardContent className="p-8">
                  <div className="text-4xl font-bold text-heritage mb-2">{stat.number}</div>
                  <div className="text-xl font-semibold text-foreground mb-2">{stat.label}</div>
                  {stat.description && (
                    <div className="text-sm text-muted-foreground">
                      <RichTextDisplay content={stat.description || ''} />
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button variant="heritage" size="lg" asChild>
              <Link to="/about-us">
                <Target className="w-5 h-5 mr-2" />
                Learn More About Us
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      {features.newsletter.enabled && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-heritage/5">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-foreground mb-6">
              {features.newsletter.title}
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              {features.newsletter.description}
            </p>
            
            <form 
              onSubmit={async (e) => {
                e.preventDefault();
                
                if (!newsletterEmail.trim()) {
                  toast.error('Please enter your email address');
                  return;
                }

                setIsSubmittingNewsletter(true);

                try {
                  const result = await newsletterService.subscribeToNewsletter({
                    email: newsletterEmail.trim(),
                  });

                  if (result.success) {
                    toast.success('Thank you for subscribing to our newsletter!');
                    setNewsletterEmail('');
                  } else {
                    toast.error(result.error?.getUserMessage() || 'Failed to subscribe. Please try again.');
                  }
                } catch (error) {
                  console.error('Error subscribing to newsletter:', error);
                  toast.error('An unexpected error occurred. Please try again.');
                } finally {
                  setIsSubmittingNewsletter(false);
                }
              }}
              className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto mb-8"
            >
              <Input 
                type="email"
                placeholder="Enter your email address" 
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                disabled={isSubmittingNewsletter}
                className="flex-1"
                aria-label="Email address for newsletter"
                required
              />
              <Button 
                type="submit"
                variant="heritage"
                disabled={isSubmittingNewsletter}
              >
                {isSubmittingNewsletter ? (
                  <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <>
                    Subscribe
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>

            <p className="text-sm text-muted-foreground">
              Join our newsletter and never miss an event or community celebration.
            </p>
          </div>
        </section>
      )}

      {/* Membership Spotlight - Only show if membership is enabled */}
      {features.membership.enabled && (
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-foreground mb-6">
                  Join Our Community
                </h2>
                <p className="text-xl text-muted-foreground mb-6">
                  Become part of a vibrant community dedicated to preserving our heritage 
                  and celebrating the diversity that makes our community special.
                </p>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-center">
                    <Star className="w-5 h-5 text-heritage mr-3" />
                    <span>Access to exclusive events and workshops</span>
                  </div>
                  <div className="flex items-center">
                    <Users className="w-5 h-5 text-heritage mr-3" />
                    <span>Connect with community members who share your passion</span>
                  </div>
                  <div className="flex items-center">
                    <BookOpen className="w-5 h-5 text-heritage mr-3" />
                    <span>Library privileges and research access</span>
                  </div>
                  {features.donation.enabled && (
                    <div className="flex items-center">
                      <Heart className="w-5 h-5 text-heritage mr-3" />
                      <span>Support preservation efforts for future generations</span>
                    </div>
                  )}
                </div>

                <Button variant="heritage" size="lg" asChild>
                  <Link to="/membership">
                    <Users className="w-5 h-5 mr-2" />
                    Join Our Community
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>

              <Card className="shadow-heritage">
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-gradient-heritage rounded-full flex items-center justify-center mx-auto mb-4">
                      <Award className="w-8 h-8 text-heritage-foreground" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground">Member Benefits</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="p-4 bg-heritage/5 rounded-lg">
                      <div className="font-medium text-foreground mb-1">Priority Registration</div>
                      <div className="text-sm text-muted-foreground">
                        Get first access to popular events and workshops
                      </div>
                    </div>
                    <div className="p-4 bg-culture/5 rounded-lg">
                      <div className="font-medium text-foreground mb-1">Community Resources</div>
                      <div className="text-sm text-muted-foreground">
                        Access to heritage library and digital archives
                      </div>
                    </div>
                    <div className="p-4 bg-accent/5 rounded-lg">
                      <div className="font-medium text-foreground mb-1">Community Network</div>
                      <div className="text-sm text-muted-foreground">
                        Connect with fellow heritage enthusiasts
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* Community Voices (Testimonials) - Content-Managed Only */}
      {features.testimonials.enabled && testimonials.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-secondary/30">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Community Voices
              </h2>
              <p className="text-lg text-muted-foreground">
                Hear from our community members about their experiences and connections
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <Card key={testimonial.id || `testimonial-${testimonial.author}-${testimonial.quote.substring(0, 20)}`} className="shadow-warm hover:shadow-heritage transition-shadow">
                  <CardContent className="p-8">
                    <Quote className="w-8 h-8 text-heritage mb-4" />
                    <p className="text-muted-foreground mb-6 italic">
                      "{testimonial.quote}"
                    </p>
                    <div className="border-t pt-4">
                      <div className="font-medium text-foreground">{testimonial.author}</div>
                      {testimonial.role && (
                        <div className="text-sm text-muted-foreground">{testimonial.role}</div>
                      )}
                      {testimonial.membership && (
                        <div className="text-xs text-heritage mt-1">
                          Member for {testimonial.membership}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Our Story (CMS-managed via About page) */}
      {aboutPage && aboutPage.content?.blocks?.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <RenderContent content={aboutPage.content} />
          </div>
        </section>
      )}

      {/* Quote Button Section - Always visible */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-heritage text-heritage-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <Button variant="secondary" size="lg" asChild>
            <Link to="/form/quote-request">
              <FileText className="w-5 h-5 mr-2" />
              Get a Free Quote
            </Link>
          </Button>
        </div>
      </section>

      {/* Final CTA Section - Only show if CTA tile exists */}
      {ctaTile && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-heritage text-heritage-foreground">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {ctaTile.title}
            </h2>
            <div className="text-xl opacity-90 mb-8">
              {ctaTile.description && <p>{ctaTile.description}</p>}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {features.membership.enabled && (
              <Button variant="secondary" size="lg" asChild>
                <Link to="/membership">
                  <Users className="w-5 h-5 mr-2" />
                  Become a Member
                </Link>
              </Button>
            )}
            {features.events.enabled && (
              <Button variant="outline" size="lg" className="bg-transparent border-heritage-foreground text-heritage-foreground hover:bg-heritage-foreground hover:text-heritage" asChild>
                <Link to="/events">
                  <Calendar className="w-5 h-5 mr-2" />
                  Explore Events
                </Link>
              </Button>
            )}
            {features.donation.enabled && (
              <Button variant="secondary" size="lg" asChild>
                <Link to="/donate">
                  <Heart className="w-5 h-5 mr-2" />
                  Support Our Mission
                </Link>
              </Button>
            )}
            {/* Fallback if no features enabled */}
            {!features.membership.enabled && !features.events.enabled && !features.donation.enabled && (
              <Button variant="secondary" size="lg" asChild>
                <Link to="/contact">
                  Get In Touch
                </Link>
              </Button>
            )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Index;
